import React, { useEffect, useRef, useState } from 'react';
import useResizeObserver from 'use-resize-observer';

import CallAvatar from 'flow/components/communication-canvas/CallAvatar';
import PinCode from 'flow/components/communication-canvas/PinCode';
import Tooltip from 'flow/components/ui-components/Tooltip';
import { TypedMap } from 'flow/types/TypedMap';
import { Contact } from 'flow/types/Contact';

interface Props {
    contact: TypedMap<Contact>;
    callCanvasMinimized: boolean;
    direction: string;
    isAccepted: boolean;
    isOnHold: boolean;
    title: string;
    subtitle: string;
    isConference: boolean;
    conferenceMeetingCode: string;
    conferencePinCode: string;
    getPinCode: (meetingCode: string) => Promise<void>;
}

const AvatarDescription = ({
    contact,
    callCanvasMinimized,
    direction,
    isAccepted,
    isOnHold,
    title,
    subtitle,
    isConference,
    conferenceMeetingCode,
    conferencePinCode,
    getPinCode,
}: Props) => {
    const avatar = (
        <CallAvatar
            contact={contact}
            size={callCanvasMinimized ? 'medium' : 'large'}
            direction={direction}
            isAccepted={isAccepted}
            isOnHold={isOnHold}
            isConference={isConference}
        />
    );

    // The row has to fit an avatar, the call state or duration and up to four
    // controls, so on a narrow canvas the caller's name and number are what
    // give way. They ellipsise rather than overflow, and hovering reveals them
    // in full — but only while they are actually cut off, so an identity that
    // already fits does not sprout a redundant tooltip.
    const titleRef = useRef<HTMLDivElement>(null);
    const subtitleRef = useRef<HTMLSpanElement>(null);
    const { ref: columnRef, width: columnWidth } =
        useResizeObserver<HTMLDivElement>();
    const [isTruncated, setIsTruncated] = useState(false);

    useEffect(() => {
        const overflows = (element: HTMLElement | null) =>
            !!element && element.scrollWidth > element.offsetWidth;

        setIsTruncated(
            overflows(titleRef.current) || overflows(subtitleRef.current),
        );
    }, [columnWidth, title, subtitle]);

    return (
        <div className="flex min-w-0 flex-1 items-center">
            <div className="mx-2 shrink-0">{avatar}</div>
            {/* Tooltip drops its wrapper entirely for an empty label, so the
                column below is a flex item of either this row or that wrapper
                depending on whether it is truncated. Both are flex-1 over a
                zero basis, so the width it measures comes from the row's free
                space either way — the measurement cannot chase its own result
                back and forth. */}
            <Tooltip
                label={
                    isTruncated ? (
                        <>
                            <div>{title}</div>
                            {subtitle ? (
                                <div className="text-gray-400">{subtitle}</div>
                            ) : null}
                        </>
                    ) : (
                        ''
                    )
                }
                tooltipClassName="min-w-0 flex-1"
            >
                {/* The title and the subtitle truncate themselves; a clip
                    here would cut the focus ring of the PIN button. */}
                <div
                    ref={columnRef}
                    className="mx-2 flex min-w-0 flex-1 flex-col justify-center whitespace-nowrap"
                >
                    <div
                        ref={titleRef}
                        className="overflow-hidden text-base/6 text-ellipsis whitespace-nowrap text-white"
                        data-test-id="title-subtitle-first-row"
                    >
                        {title}
                    </div>
                    <div
                        className="flex items-center gap-x-1 text-xs/6 text-gray-400"
                        data-test-id="title-subtitle-second-row"
                    >
                        {/* `text-overflow: ellipsis` does not apply to a flex
                            container, so the subtitle needs its own block-level
                            box to truncate in — otherwise it is hard-clipped
                            mid-glyph. The row stays flex to align PinCode. */}
                        <span ref={subtitleRef} className="truncate">
                            {subtitle}
                        </span>
                        {isConference ? (
                            <PinCode
                                meetingCode={conferenceMeetingCode}
                                pinCode={conferencePinCode}
                                onClick={getPinCode}
                            />
                        ) : null}
                    </div>
                </div>
            </Tooltip>
        </div>
    );
};

export default React.memo(AvatarDescription);
